import api from './enhancedApiService'
import { isPrivacyCategoryAllowed } from '@/privacy/privacyPreferences'
import {
  isSensitiveTelemetryRoute,
  sanitizeTelemetryUrl
} from '@/privacy/telemetrySanitizer'

const SLOW_NAV_MS = parseInt(import.meta.env.VITE_SLOW_NAV_MS || '1500')

/**
 * Check if user is authenticated (for analytics tracking)
 * Only track analytics for authenticated users to protect sensitive data
 */
function isAuthenticated() {
  try {
    // Check localStorage for cached user data
    const cachedUser = localStorage.getItem('user')
    if (cachedUser) {
      try {
        const user = JSON.parse(cachedUser)
        return !!user && !!user.id
      } catch {
        return false
      }
    }
    return false
  } catch {
    return false
  }
}

// Track recent page views to prevent duplicate calls (e.g., from React StrictMode)
const recentPageViews = new Map()
const DEDUPE_WINDOW_MS = 1000 // 1 second deduplication window

export function trackPageView(path, referrer = document.referrer) {
  if (!isPrivacyCategoryAllowed('analytics')) return
  if (isSensitiveTelemetryRoute(path)) return

  // Only track analytics for authenticated users
  if (!isAuthenticated()) {
    return
  }

  // Deduplicate: prevent tracking the same path within 1 second
  const now = Date.now()
  const lastTracked = recentPageViews.get(path)
  if (lastTracked && (now - lastTracked) < DEDUPE_WINDOW_MS) {
    return // Skip duplicate call
  }
  recentPageViews.set(path, now)

  // Clean up old entries (keep map size reasonable)
  if (recentPageViews.size > 50) {
    const oldestKey = recentPageViews.keys().next().value
    recentPageViews.delete(oldestKey)
  }

  try {
    const metadata = {
      url: sanitizeTelemetryUrl(window.location.origin + path),
      path: sanitizeTelemetryUrl(path),
      referrer: referrer ? sanitizeTelemetryUrl(referrer) : undefined,
      title: document.title,
      loadTime: performance.getEntriesByType('navigation')[0]?.duration
    }
    // Use void to fire-and-forget, catch errors silently
    void api.post('/analytics/ingest', { eventType: 'PAGE_VIEW', metadata }).catch(() => {
      // Silently ignore analytics errors - don't break the app
    })
  } catch (_e) {
    // Silently ignore analytics errors
  }
}

// Track if we've already set up route tracking to prevent duplicates
let routeTrackingSetup = false
let wrappedNavigate = null

export function bindRouteChangeTracking(navigate, getLocation) {
  // Prevent duplicate setup
  if (routeTrackingSetup && wrappedNavigate) {
    return wrappedNavigate
  }

  let lastPath = getLocation().pathname
  let navStart = performance.now()
  
  // Only track initial page view if authenticated
  if (
    isAuthenticated() &&
    isPrivacyCategoryAllowed('analytics') &&
    !isSensitiveTelemetryRoute(lastPath)
  ) {
    trackPageView(lastPath)
  }

  // Wrap the navigate function to track route changes
  wrappedNavigate = function onNavigate(...args) {
    const prevPath = lastPath
    navStart = performance.now()
    const result = navigate(...args)
    const newPath = getLocation().pathname
    lastPath = newPath
    
    // Only track if authenticated
    if (
      isAuthenticated() &&
      isPrivacyCategoryAllowed('analytics') &&
      !isSensitiveTelemetryRoute(newPath)
    ) {
      const duration = performance.now() - navStart
      if (duration >= SLOW_NAV_MS) {
        void api.post('/analytics/ingest', {
          eventType: 'CLIENT_NAVIGATION_SLOW',
          metadata: { path: newPath, referrer: prevPath, duration, threshold: SLOW_NAV_MS, type: 'route-change' }
        }).catch(() => {
          // Silently ignore analytics errors
        })
      }
      trackPageView(newPath, window.location.origin + prevPath)
    }
    
    return result
  }

  routeTrackingSetup = true
  return wrappedNavigate
}
